// React imports
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// MUI imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkSlash, faInfoCircle, faPlus } from '@fortawesome/pro-light-svg-icons';

// Generic imports
import _ from 'lodash';

// Custom imports
import currencyToText from '../../CurrencyToText';
import CurrencyTextField from '../components/CurrencyTextField';
import { parseFloatOrString, multiplyIfFloat } from './OpportunityOneOffBuilder.utils.js';
import { calcFieldValue } from '../../utils/opportunity.js';
import { formulaToParams, formulaToReact } from '../../utils/Formula.js';
import { ratesToText, getRatesString, getTotalRates, getFieldRatesForProduct } from '../utils/rates.js';
import { FieldTypes } from '../../FieldTypes.js';
import FormulaEditorModal from '../components/FormulaEditorModal.js';

// Monada imports
import SupplierLogos from '@monada-ai/monada-shipping-companies-logos';

function OpportunityOfferTextField(props) {
    const { field, setField, products, defaultCurrency, buyingRates, fieldBuyingRates, transportationMethod, 
        disabled, sections, onFieldSettings, exchangeRates, lockedExchangeRates, organization, supplierRatesData, onShowRates, fieldId } = props;
    const [ focused, setFocused ] = useState(null);
    const [ formulaDialogOpen, setFormulaDialogOpen ] = useState(null);

    const values = useMemo(() => field.values || {}, [field.values]);
    function setValues(v) {
        setField(f => {
            if (_.isFunction(v)) return ({...f, values: v(f.values)});
            return ({ ...f, values: _.cloneDeep(v) });
        });
    }

    function onAddMargin({ chargeLine }) {
        setFormulaDialogOpen(`${chargeLine.fieldValueId}-uplift`);
    }

    function onFormulaDone({ chargeLine, formula, currency }) {
        const _values = _.cloneDeep(values);
        const fieldCurrency = currency || _values[chargeLine.fieldValueId]?.currency || defaultCurrency;
        
        _values[chargeLine.fieldValueId] = {
            ..._values[chargeLine.fieldValueId],
            formula: formula,
            currency: fieldCurrency
        };
        // Remove value since we're using formula now
        delete _values[chargeLine.fieldValueId].value;
        delete _values[chargeLine.fieldValueId].sumValue;
        delete _values[chargeLine.fieldValueId].multiplier;
        
        (chargeLine.productsInChargeLine || []).forEach(p => {
            _values[p.id] = {
                ..._values[p.id],
                formula: formula,
                currency: fieldCurrency
            };
            // Remove value since we're using formula now
            delete _values[p.id].value;
            delete _values[p.id].sumValue;
            delete _values[p.id].multiplier;
        });
        
        setField({ ...field, values: _values });
        setFormulaDialogOpen(null);
    }

    function onBlur(focusedId) {
        if (focused === focusedId || focused === `${focusedId}-sum`) {
            const _values = _.mapValues(values, v => {
                if (_.isUndefined(v.sumValue)) return v;
                return _.omit({ ...v, value: parseFloatOrString(v.sumValue, v.multiplier) }, [ 'sumValue', 'multiplier' ]);
            });
            setField({ ...field, values: _values })
            setFocused(null);
        }
    }

    if (field.type === 'string') {
        const stringRates = getRatesString({ fieldBuyingRates, products, buyingRates, transportationMethod });
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start', minWidth: 0, maxWidth: '100%' }} >
                <TextField
                    fullWidth
                    value={(values.string || {}).value || ''}
                    onChange={e => setValues(vs => ({ ...vs, string: { value: e.target.value } }))}
                    onFocus={() => setFocused('string')}
                    onBlur={e => { onBlur('string') }}
                    disabled={disabled}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                            '&.Mui-focused': {
                                '& fieldset': {
                                    borderColor: '#3b82f6',
                                },
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                            },
                        },
                    }}
                />
                {stringRates.length > 0 && (
                    <Alert severity='info' sx={{ fontSize: '11px' }}>
                        {stringRates.join(' | ')}
                    </Alert>
                )}
            </Box>
        )
    }

    const fieldTypeDefinition = FieldTypes[transportationMethod].find(ft => ft.value === field.type);
    if (!fieldTypeDefinition) return null;

    const chargeLines = fieldTypeDefinition.getChargeLines({ field, products, buyingRates, fieldBuyingRates, organization });

    // Find the active chargeLine for the formula dialog
    const activeChargeLine = chargeLines.find(cl => formulaDialogOpen === `${cl.fieldValueId}-uplift`);
    const activeFormula = activeChargeLine ? (values[activeChargeLine.fieldValueId] || {}).formula : null;
    const activeCurrency = activeChargeLine ? ((values[activeChargeLine.fieldValueId] || {}).currency || defaultCurrency) : defaultCurrency;

    if (field.type === 'custom') {
        return (
            <>
                <CustomCell 
                    values={values}
                    setValues={setValues}
                    defaultCurrency={defaultCurrency}
                    focused={focused}
                    setFocused={setFocused}
                    onBlur={onBlur}
                    disabled={disabled}
                    onFieldSettings={onFieldSettings}
                    buyingRateText={ratesToText({ rates: chargeLines[0].buyingRates, transportationMethod, buyingRates, exchangeRates, products, organization })}
                    organization={organization}
                    onAddMargin={() => onAddMargin({ chargeLine: chargeLines[0] })}
                    supplierRatesData={supplierRatesData}
                    onShowRates={onShowRates}
                    fieldId={fieldId}
                    fieldBuyingRates={fieldBuyingRates}
                />
                {activeChargeLine && (
                    <FormulaEditorModal
                        open={!!formulaDialogOpen}
                        onClose={() => setFormulaDialogOpen(null)}
                        value={activeFormula || ''}
                        onDone={result => {
                            // Handle both string (formula) and object ({ formula, currency }) formats
                            const formula = typeof result === 'string' ? result : result.formula;
                            const currency = typeof result === 'object' && result.currency ? result.currency : activeCurrency;
                            onFormulaDone({ chargeLine: activeChargeLine, formula, currency });
                        }}
                        sections={sections}
                        noTax={field.type !== 'flat'}
                        withBuyingMargin={true}
                        initialTab={0}
                        hideTabs={true}
                        initialFixedOrTier='buying-margin'
                        currency={activeCurrency}
                        organization={organization}
                    />
                )}
            </>
        )
    }

    return (
        <>
            <Table>
                <TableBody>
                    {chargeLines.map(chargeLine => (
                        <CurrencyTextFieldRowWithSum
                            key={chargeLine.fieldValueId}
                            field={field}
                            setField={setField}
                            transportationMethod={transportationMethod}
                            products={chargeLine.productsInChargeLine}
                            allProducts={products}
                            multiplier={chargeLine.quantityMultiplier}
                            multiplierText={chargeLine.multiplierText}
                            multiplierTextSingular={chargeLine.multiplierTextSingular}
                            precell={chargeLine.title}
                            subprecell={chargeLine.description}
                            values={values}
                            setValues={setValues}
                            defaultCurrency={defaultCurrency}
                            focused={focused}
                            setFocused={setFocused}
                            onBlur={onBlur}
                            buyingRates={buyingRates}
                            buyingRateText={ratesToText({ rates: chargeLine.buyingRates, transportationMethod, buyingRates, exchangeRates, products, organization })}
                            disabled={disabled}
                            onFieldSettings={onFieldSettings}
                            sections={sections}
                            exchangeRates={exchangeRates}
                            lockedExchangeRates={lockedExchangeRates}
                            organization={organization}
                            onAddMargin={() => onAddMargin({ chargeLine })}
                            supplierRatesData={(() => {
                                if (!chargeLine.buyingRates?.length) return [];
                                return supplierRatesData.map(sd => {
                                    const supplierBuyingRates = chargeLine.buyingRates.filter(br => br.rate?.supplier?.organization === sd.supplier);
                                    return {
                                        ...sd,
                                        ratesText: supplierBuyingRates.length
                                            ? ratesToText({ rates: supplierBuyingRates, transportationMethod, buyingRates, exchangeRates, products: chargeLine.productsInChargeLine, organization })
                                            : []
                                    };
                                }).filter(sd => sd.ratesText.length > 0);
                            })()}
                            onShowRates={onShowRates}
                            fieldId={fieldId}
                            fieldBuyingRates={fieldBuyingRates}
                            fieldValueId={chargeLine.fieldValueId}
                        />
                    ))}
                </TableBody>
            </Table>
            {activeChargeLine && (
                <FormulaEditorModal
                    open={!!formulaDialogOpen}
                    onClose={() => setFormulaDialogOpen(null)}
                    value={activeFormula || ''}
                    onDone={result => {
                        // Handle both string (formula) and object ({ formula, currency }) formats
                        const formula = typeof result === 'string' ? result : result.formula;
                        const currency = typeof result === 'object' && result.currency ? result.currency : activeCurrency;
                        onFormulaDone({ chargeLine: activeChargeLine, formula, currency });
                    }}
                    sections={sections}
                    noTax={field.type !== 'flat'}
                    withBuyingMargin={true}
                    initialTab={0}
                    hideTabs={true}
                    initialFixedOrTier='buying-margin'
                    currency={activeCurrency}
                    organization={organization}
                />
            )}
        </>
    );
}

function CustomCell(props) {
    const { values, setValues, focused, setFocused, onBlur, defaultCurrency, disabled, onFieldSettings, buyingRateText, organization, onAddMargin, supplierRatesData, onShowRates, fieldId, fieldBuyingRates } = props;
    const theme = useTheme();

    const itemDescription = (values.custom || {}).multiplierText || '';
    const numItems = (values.custom || {}).multiplier || 1;

    function setItemDescription(value) {
        const _values = { custom: { ...(values.custom || {}), multiplierText: value } };
        setValues(vs => ({...vs, ..._values}));
    }

    function setNumItems(value) {
        const _values = { custom: { ...(values.custom || {}), multiplier: parseFloatOrString(value) } };
        setValues(vs => ({...vs, ..._values}));
    }

    return (
        <Table size='small'>
            <TableBody>
                <TableRow sx={{ borderBottom: 'none' }}>
                    <TableCell sx={{ borderBottom: 'none' }}>
                        <Tooltip title="Price per item">
                            <span>
                        <CurrencyTextField
                            forceTextField={true}
                            value={(values.custom || {}).value || ''}
                            setValue={(v) => {
                                const _values = { custom: { ...(values.custom || {}), value: parseFloatOrString(v), currency: (values.custom || {}).currency || defaultCurrency } };
                                setValues(vs => ({...vs, ..._values}));
                            }}
                            focusedId={'custom'}
                            focused={focused}
                            setFocused={setFocused}
                            currency={(values.custom || {}).currency || defaultCurrency}
                            setCurrency={(v) => {
                                const _values = { custom: { ...(values.custom || {}), currency: v } };
                                setValues(vs => ({...vs, ..._values}));
                            }}
                            onBlur={onBlur}
                            disabled={disabled}
                            organization={organization}
                        />
                            </span>
                        </Tooltip>
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                        X
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', minWidth: '100px' }}>
                        <Tooltip title="# items">
                            <span>
                        <TextField
                            value={numItems}
                            onChange={e => setNumItems(e.target.value)}
                            onFocus={() => setFocused('custom-quantity')}
                            onBlur={e => {
                                onBlur('custom-quantity');
                            }}
                            disabled={disabled}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                                            '&.Mui-focused': {
                                                '& fieldset': {
                                                    borderColor: '#3b82f6',
                                                },
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                            },
                                        },
                                    }}
                                />
                            </span>
                        </Tooltip>
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', paddingLeft: 0, width: '200px', minWidth: '200px' }}>
                        <Tooltip title='items description (eg. "invoices")'>
                        <TextField
                                fullWidth
                            value={itemDescription}
                            onChange={e => setItemDescription(e.target.value)}
                            onFocus={() => setFocused('custom-description')}
                            onBlur={e => {
                                onBlur('custom-description');
                            }}
                            disabled={disabled}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#ffffff',
                                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                                        '&.Mui-focused': {
                                            '& fieldset': {
                                                borderColor: '#3b82f6',
                                            },
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        textAlign: 'left',
                                    },
                                }}
                            />
                        </Tooltip>
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                        =
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                        <Tooltip title="Total">
                            <span>
                        <CurrencyTextField
                            forceTextField={true}
                            value={((values.custom || {}).value || 0) * (parseFloat(numItems) || 1)}
                            setValue={(v) => {
                                const _values = { custom: { ...(values.custom || {}), value: parseFloatOrString(v) / (parseFloat(numItems) || 1), currency: (values.custom || {}).currency || defaultCurrency } };
                                setValues(vs => ({...vs, ..._values}));
                            }}
                            focusedId={'custom-sum'}
                            focused={focused}
                            setFocused={setFocused}
                            currency={(values.custom || {}).currency || defaultCurrency}
                            setCurrency={(v) => {
                                const _values = { custom: { ...(values.custom || {}), currency: v } };
                                setValues(vs => ({...vs, ..._values}));
                            }}
                            onBlur={onBlur}
                            disabled={disabled}
                            organization={organization}
                        />
                            </span>
                        </Tooltip>
                    </TableCell>
                </TableRow>
                {(!!(_.get(values, 'custom.value', '') && _.isNaN(parseFloat(values.custom.value)))) && (
                    <TableRow>
                        <TableCell colSpan={6} sx={{ paddingTop: '6px' }}>
                            <Alert severity='error' sx={{ fontSize: '11px' }}>
                                This box is for numbers only...
                            </Alert>
                        </TableCell>
                    </TableRow>
                )}
                {supplierRatesData && supplierRatesData.length > 0 && (
                    <TableRow>
                        <TableCell colSpan={6} sx={{ paddingTop: '6px', borderBottom: 'none' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {supplierRatesData.map(({ supplier, ratesText }, index) => (
                                    <Alert 
                                        key={supplier}
                                        severity='info' 
                                        icon={false}
                                        sx={{ 
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.info.main, 0.12),
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                            }
                                        }}
                                        onClick={() => onShowRates && onShowRates({ fieldBuyingRates, supplier, fieldId })}
                                    >
                                        <AlertTitle sx={{ fontSize: '11px', fontWeight: 400, marginBottom: ratesText.length > 0 ? '4px' : '0px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <SupplierLogos size={SupplierLogos.size.MINI} organization={supplier} />
                                                <Typography sx={{ fontSize: '11px', fontWeight: 400 }}>
                                                    {supplier}
                                                </Typography>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 400, color: theme.palette.text.secondary }}>
                                                    supplier
                                                </Typography>
                                </Box>
                                        </AlertTitle>
                                        {ratesText.length > 0 && (
                                            <Box sx={{ marginTop: '4px' }}>
                                                <span dangerouslySetInnerHTML={{ __html: ratesText.join('') }} />
                                </Box>
                                        )}
                            </Alert>
                                ))}
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
                    <TableRow>
                    <TableCell colSpan={6} sx={{ paddingTop: '2px', borderBottom: 'none', backgroundColor: '#ffffff' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px', alignItems: 'center' }}>
                            <Button
                                variant='text'
                                onClick={onAddMargin}
                                sx={{
                                    textTransform: 'uppercase',
                                    fontSize: '11px !important',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    color: theme.palette.text.primary,
                                    border: 'none',
                                    borderBottom: '2px solid transparent',
                                    borderRadius: 0,
                                    padding: '4px 0px',
                                    minWidth: 'auto',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    '& .MuiButton-label': {
                                        fontSize: '11px !important'
                                    },
                                    '& span': {
                                        fontSize: '11px !important'
                                    },
                                    '& .MuiButton-text': {
                                        fontSize: '11px !important'
                                    },
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                        backgroundColor: 'transparent',
                                        '& .MuiButton-label': {
                                            fontSize: '11px !important'
                                        },
                                        '& span': {
                                            fontSize: '11px !important'
                                        }
                                    }
                                }}
                            >
                                Price with Uplift
                            </Button>
                            <Button
                                variant='text'
                                onClick={onFieldSettings}
                                sx={{
                                    textTransform: 'uppercase',
                                    fontSize: '11px !important',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    color: theme.palette.text.primary,
                                    border: 'none',
                                    borderBottom: '2px solid transparent',
                                    borderRadius: 0,
                                    padding: '4px 0px',
                                    minWidth: 'auto',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    '& .MuiButton-label': {
                                        fontSize: '11px !important'
                                    },
                                    '& span': {
                                        fontSize: '11px !important'
                                    },
                                    '& .MuiButton-text': {
                                        fontSize: '11px !important'
                                    },
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                        backgroundColor: 'transparent',
                                        '& .MuiButton-label': {
                                            fontSize: '11px !important'
                                        },
                                        '& span': {
                                            fontSize: '11px !important'
                                        }
                                    }
                                }}
                            >
                                Add or Remove Costs
                            </Button>
                        </Box>
                        </TableCell>
                    </TableRow>
            </TableBody>
        </Table>
    );
}

function CurrencyTextFieldRowWithSum(props) {
    const { field, setField, transportationMethod, products, allProducts, precell, subprecell, multiplier, 
        multiplierText, multiplierTextSingular, values, setValues, defaultCurrency, focused, setFocused, 
        onBlur, buyingRateText, disabled, sections, onFieldSettings, exchangeRates, lockedExchangeRates, 
        organization, onAddMargin, buyingRates, supplierRatesData, onShowRates, fieldId, fieldBuyingRates, fieldValueId } = props;

    // For "priced per type" fields, filter supplierRatesData to only show rates for the products in this charge line
    let filteredSupplierRatesData = supplierRatesData;
    if ((field.type === 'per-unit-type' || field.type === 'per-unit-type-weight') && fieldBuyingRates && products.length > 0) {
        // Get the product IDs for this specific charge line
        const productIds = new Set(products.map(p => p.id));
        
        // Filter fieldBuyingRates to only include rates where opportunityProductId exactly matches one of the products in this charge line
        const filteredFieldBuyingRates = fieldBuyingRates.filter(fb => 
            productIds.has(fb.opportunityProductId)
        );
        
        // Get suppliers for the filtered rates
        const filteredSuppliers = _(filteredFieldBuyingRates)
            .map(fb => buyingRates.find(r => r.id === fb.rateId))
            .compact()
            .map(r => r.supplier.organization)
            .uniq()
            .value();
        
        // Recalculate supplierRatesData for just the filtered suppliers and products
        filteredSupplierRatesData = filteredSuppliers.map(supplier => {
            // Filter to only this supplier's rates
            const supplierFieldBuyingRates = filteredFieldBuyingRates.filter(fb => {
                const rate = buyingRates.find(r => r.id === fb.rateId);
                return rate && rate.supplier.organization === supplier;
            });
            
            // For each product in this charge line, get only rates where opportunityProductId matches that specific product
            const allRates = _.flatten(products.map(product => {
                // Further filter to only rates connected to this specific product
                const productSpecificFieldBuyingRates = supplierFieldBuyingRates.filter(fb => 
                    fb.opportunityProductId === product.id
                );
                
                return getFieldRatesForProduct({ 
                    fieldBuyingRates: productSpecificFieldBuyingRates, 
                    product, 
                    buyingRates, 
                    transportationMethod, 
                    allProducts: products, 
                    organization 
                });
            })).filter(Boolean);
            
            const ratesText = allRates.length > 0 ? ratesToText({ rates: allRates, transportationMethod, exchangeRates, organization }) : [];
            
            return { supplier, ratesText };
        }).filter(s => s.ratesText.length > 0); // Only include suppliers that have rates for these products
    }

    // products may be empty for a charge row whose product is unset / has been
    // removed. Fall back to 'custom' so the cell renders instead of throwing
    // a "Cannot read properties of undefined (reading 'id')" render-time error.
    const valueId = fieldValueId !== undefined ? fieldValueId : (field.type === 'flat' ? 'flat' : (products[0]?.id || 'custom'));

    // Derive a buying-rate currency fallback: if the linked buying rates for this field all share
    // a single currency, use it instead of the org default when the user hasn't set a currency yet.
    const buyingRateCurrency = (() => {
        if (!fieldBuyingRates || !buyingRates) return null;
        const currencies = _(fieldBuyingRates)
            .map(fb => {
                const rate = buyingRates.find(r => r.id === fb.rateId);
                if (!rate) return null;
                const rateField = _.flatten((rate.offer?.sections || []).map(s => _.flatten((s.offers || []).map(o => o.fields || [])))).find(f => f.id === fb.fieldId);
                if (!rateField) return null;
                const v = rateField.values || {};
                return v.flat?.currency || v[fb.opportunityProductId]?.currency || v[rate.product?.id]?.currency || _.values(v)[0]?.currency;
            })
            .compact()
            .uniq()
            .value();
        return currencies.length === 1 ? currencies[0] : null;
    })();
    const effectiveDefaultCurrency = buyingRateCurrency || defaultCurrency;

    let value = (values[valueId] || {}).value;
    if (!value && value !== 0) value = '';

    let sumValue = (values[valueId] || {}).sumValue;
    if (_.isUndefined(sumValue)) {
        let p = parseFloat(value);
        if (_.isNaN(p)) p = value;
        sumValue = multiplyIfFloat(p, multiplier);
    }

    const formula = (values[valueId] || {}).formula;
    if (formula) {

        let displayCurrency = field.displayCurrency;
        if (_.isObject(displayCurrency)) {
            if (displayCurrency.convert) {
                displayCurrency = displayCurrency.to;
            } else {
                displayCurrency = null;
            }
        }
        if (!displayCurrency) displayCurrency = (values[valueId] || {}).currency || effectiveDefaultCurrency;

        const calcValue = calcFieldValue({ field: { ...field, displayCurrency }, products, sections, transportationMethod, organization, buyingRates, fieldBuyingRates: field.buyingRates, exchangeRates, lockedExchangeRates });

        const formulaValue = calcValue ? calcValue.sum[displayCurrency] || 0 : 0;

        function onBreakFormula() {
            // Get the calculated value for the current currency
            let valueForCurrency = formulaValue;
            
            // For non-flat fields, divide by multiplier to get per-unit price
            if (field.type !== 'flat' && multiplier && multiplier > 0) {
                valueForCurrency = valueForCurrency / multiplier;
            }
            
            // For "priced per type" fields, only update the specific product being edited
            // For other field types, update all products in the charge line
            const ps = field.type === 'per-unit-type' ? [{ id: valueId }] : (field.type === 'flat' ? [{ id: 'flat' }] : products);
            const _values = _(ps).map(w => (
                [ w.id, 
                    { 
                        ...(values[w.id] || {}), 
                        value: valueForCurrency,
                        currency: displayCurrency
                    }
                ]
            )).fromPairs().value();
            
            // Remove formula-related properties only for the products being updated
            ps.forEach(p => {
                if (_values[p.id]) {
                    delete _values[p.id].formula;
                    delete _values[p.id].sumValue;
                    delete _values[p.id].multiplier;
                }
            });
            
            // Merge with existing values to preserve other products' formulas
            const mergedValues = { ...values, ..._values };
            
            setValues(mergedValues);
            // Persist the changes to the parent component so they are saved
            setField({ ...field, values: mergedValues });
        }

        const attempt = formulaToParams({ formula });
        const isUplift = attempt && attempt.buying;
        const formulaType = isUplift ? 'Uplift' : 'Formula';

        const formulaExplanation = formulaToReact({
            formula: formula,
            currency: values[valueId].currency,
            errorValue: formula,
            multiplierText: multiplierTextSingular,
            sections,
            width: 'auto',
        });

        // Format the calculated value
        const calculatedValueStr = calcValue?.sum ? _.map(calcValue.sum, (value, curr) => {
            return currencyToText(curr, true, Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value));
        }).join(', ') : null;

        return ([
            <TableRow key='formula'>
                {precell && (
                    <TableCell sx={{ borderBottom: '0px', paddingBottom: '0px', padding: '4px 8px' }}>
                        <Typography sx={{ fontSize: '0.75rem' }}>
                            {precell}
                        </Typography>
                        <Typography variant='caption' component={Box}>
                            {subprecell}
                        </Typography>
                    </TableCell>
                )}
                <TableCell sx={{ borderBottom: '0px', paddingBottom: '0px', padding: '4px 8px' }} colSpan={precell ? 3 : 4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: '8px' }}>
                        {calculatedValueStr ? (
                            <Tooltip title={formulaExplanation} arrow>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <Typography sx={{ textDecoration: 'underline', display: 'inline', fontSize: '0.75rem' }} variant='body1'>
                                        {calculatedValueStr} based on {formulaType}
                                    </Typography>
                                    <sup>
                                        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '11px', verticalAlign: 'super' }} />
                                    </sup>
                                </span>
                            </Tooltip>
                        ) : (
                            <Typography sx={{ fontSize: '14px', fontStyle: 'italic', color: 'text.secondary' }}>
                                Calculating...
                            </Typography>
                        )}
                    {!disabled && (
                        <Button
                            variant='contained'
                                sx={{ textTransform: 'none', minWidth: 'auto', fontSize: '11px !important', whiteSpace: 'nowrap' }}
                            onClick={onBreakFormula}
                            startIcon={<FontAwesomeIcon icon={faLinkSlash} style={{ fontSize: '12px' }} />}
                        >
                            Break Formula
                        </Button>
                    )}
                    </Box>
                </TableCell>
            </TableRow>,
            <BuyingRateRow key='buying-rate' precell={precell} buyingRateText={buyingRateText} onFieldSettings={onFieldSettings} onAddMargin={onAddMargin} supplierRatesData={filteredSupplierRatesData} onShowRates={onShowRates} fieldBuyingRates={fieldBuyingRates} fieldId={fieldId} />
        ])
    }

    const kgToTon = transportationMethod === 'sea' && multiplierText === 'kg' && multiplier >= 1000;

    return _.compact([
        <TableRow key='row'>
            {precell && (
                <TableCell sx={ buyingRateText ? { borderBottom: '0px', paddingBottom: '0px', padding: '4px 8px' } : { borderBottom: '0px', padding: '4px 8px' } }>
                    <Typography sx={{ fontSize: '0.75rem' }}>
                    {precell}
                    </Typography>
                    <Typography variant='caption' component={Box}>
                        {subprecell}
                    </Typography>
                </TableCell>
            )}
            <TableCell sx={ buyingRateText ? { borderBottom: '0px', paddingBottom: '0px', padding: '4px 8px' } : { borderBottom: '0px', padding: '4px 8px' } }>
                <CurrencyTextField
                    forceTextField={true}
                    value={multiplyIfFloat(value, kgToTon ? 1000 : 1)}
                    setValue={(v) => {
                        const ps = field.type === 'flat' ? [{ id: 'flat' }] : products;
                        const _values = _(ps).map(w => (
                            [ w.id, 
                                { ...(values[w.id] || {}), value: parseFloatOrString(v, kgToTon ? 1000 : 1), currency: (values[w.id] || {}).currency || effectiveDefaultCurrency }
                            ])).fromPairs().value();
                        setValues(vs => ({...vs, ..._values}));
                    }}
                    focusedId={`${valueId}`}
                    focused={focused}
                    setFocused={setFocused}
                    currency={(values[valueId] || {}).currency || effectiveDefaultCurrency}
                    setCurrency={(v) => {
                        const ps = field.type === 'flat' ? [{ id: 'flat' }] : products;
                        const _values = _(ps).map(w => ( [ w.id, { ...(values[w.id] || {}), currency: v } ])).fromPairs().value();
                        setValues(vs => ({...vs, ..._values}));
                    }}
                    onBlur={onBlur}
                    disabled={disabled}
                    organization={organization}
                />
            </TableCell>
            <TableCell sx={ buyingRateText ? { borderBottom: '0px', paddingBottom: '0px', padding: '4px 8px' } : { borderBottom: '0px', padding: '4px 8px' } }>
                <Typography variant='caption' sx={{ whiteSpace: 'nowrap' }}>
                    &nbsp;x {(kgToTon ? multiplier / 1000 : multiplier).toLocaleString()} {kgToTon ? 'ton' : multiplierText} =&nbsp;
                </Typography>
            </TableCell>
            <TableCell sx={ buyingRateText ? { borderBottom: '0px', paddingBottom: '0px', padding: '4px 8px' } : { borderBottom: '0px', padding: '4px 8px' } }>
                {field.type !== 'flat'
                    ? <CurrencyTextField
                        forceTextField={true}
                        value={sumValue}
                        setValue={(v) => {
                            const ps = field.type === 'flat' ? [{ id: 'flat' }] : products;
                            const _values = _(ps).map(w => (
                                [ w.id, 
                                    { ...(values[w.id] || {}), multiplier, sumValue: parseFloatOrString(v, kgToTon ? 1000 : 1), currency: (values[w.id] || {}).currency || effectiveDefaultCurrency }
                                ])).fromPairs().value();
                            setValues(vs => ({...vs, ..._values}));
                        }}
                        focusedId={`${valueId}-sum`}
                        focused={focused}
                        setFocused={setFocused}
                        currency={(values[valueId] || {}).currency || effectiveDefaultCurrency}
                        setCurrency={(v) => {
                            const _values = _(products).map(w => ( [ w.id, { ...(values[w.id] || {}), currency: v } ])).fromPairs().value();
                            setValues(vs => ({...vs, ..._values}));
                        }}
                        onBlur={focusedId => {
                            console.log('onBlur', sumValue);
                            const _values = _(products).map(w => (
                                [ w.id, 
                                    { ...(values[w.id] || {}), value: parseFloatOrString(sumValue, multiplier), currency: (values[w.id] || {}).currency || effectiveDefaultCurrency }
                                ])).fromPairs().value();
                            setValues(vs => ({...vs, ..._values}));
                            onBlur(focusedId);
                        }}
                        disabled={disabled}
                        organization={organization}
                    /> 
                    : <>{currencyToText(values.flat?.currency, true, (values.flat?.value || 0).toLocaleString({maximumFractionDigits: 2}))}</>}
            </TableCell>
        </TableRow>,
        (!value || !_.isNaN(parseFloat(value))) ? null : (
            <TableRow key='error'>
                <TableCell colSpan={6} sx={{ paddingTop: '6px' }}>
                    <Alert severity='error' sx={{ fontSize: '11px' }}>
                        This box is for numbers only...
                    </Alert>
                </TableCell>
            </TableRow>
        ),
        <BuyingRateRow key='buying-rate' precell={precell} buyingRateText={buyingRateText} onFieldSettings={onFieldSettings} onAddMargin={onAddMargin} supplierRatesData={filteredSupplierRatesData} onShowRates={onShowRates} fieldBuyingRates={fieldBuyingRates} fieldId={fieldId} />
    ])
}

function BuyingRateRow(props) {
    const { precell, buyingRateText, onFieldSettings, onAddMargin, supplierRatesData, onShowRates, fieldBuyingRates, fieldId } = props;
    const theme = useTheme();
    
    if (!supplierRatesData || supplierRatesData.length === 0) { 
        return (
            <TableRow>
                <TableCell colSpan={precell ? 4 : 3} sx={{ paddingTop: '2px', borderBottom: 'none', backgroundColor: '#ffffff' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px', alignItems: 'center' }}>
                        <Button
                            variant='text'
                            onClick={onAddMargin}
                            sx={{
                                textTransform: 'uppercase',
                                fontSize: '11px !important',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                color: theme.palette.text.primary,
                                border: 'none',
                                borderBottom: '2px solid transparent',
                                borderRadius: 0,
                                padding: '4px 0px',
                                minWidth: 'auto',
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '& .MuiButton-label': {
                                    fontSize: '11px !important'
                                },
                                '& span': {
                                    fontSize: '11px !important'
                                },
                                '& .MuiButton-text': {
                                    fontSize: '11px !important'
                                },
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    backgroundColor: 'transparent',
                                    '& .MuiButton-label': {
                                        fontSize: '11px !important'
                                    },
                                    '& span': {
                                        fontSize: '11px !important'
                                    }
                                }
                            }}
                        >
                            Price with Uplift
                        </Button>
                        <Button
                            variant='text'
                            onClick={onFieldSettings}
                            sx={{
                                textTransform: 'uppercase',
                                fontSize: '11px !important',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                color: theme.palette.text.primary,
                                border: 'none',
                                borderBottom: '2px solid transparent',
                                borderRadius: 0,
                                padding: '4px 0px',
                                minWidth: 'auto',
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '& .MuiButton-label': {
                                    fontSize: '11px !important'
                                },
                                '& span': {
                                    fontSize: '11px !important'
                                },
                                '& .MuiButton-text': {
                                    fontSize: '11px !important'
                                },
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    backgroundColor: 'transparent',
                                    '& .MuiButton-label': {
                                        fontSize: '11px !important'
                                    },
                                    '& span': {
                                        fontSize: '11px !important'
                                    }
                                }
                            }}
                        >
                            Add or Remove Costs
                        </Button>
                    </Box>
                </TableCell>
            </TableRow>
        );
    } else {
        return (
            <>
                {supplierRatesData.map(({ supplier, ratesText }, index) => (
                    <TableRow key={supplier}>
                        <TableCell colSpan={precell ? 4 : 3} sx={{ paddingTop: index === 0 ? '2px' : '4px', paddingBottom: '0px', borderBottom: 'none' }}>
                            <Alert 
                                severity='info' 
                                icon={false}
                                sx={{ 
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.info.main, 0.12),
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                    }
                                }}
                                onClick={() => onShowRates && onShowRates({ fieldBuyingRates, supplier, fieldId })}
                            >
                                <AlertTitle sx={{ fontSize: '11px', fontWeight: 400, marginBottom: ratesText.length > 0 ? '4px' : '0px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <SupplierLogos size={SupplierLogos.size.MINI} organization={supplier} />
                                        <Typography sx={{ fontSize: '11px', fontWeight: 400 }}>
                                            {supplier}
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 400, color: theme.palette.text.secondary }}>
                                            supplier
                                        </Typography>
                        </Box>
                                </AlertTitle>
                                {ratesText.length > 0 && (
                                    <Box sx={{ marginTop: '4px' }}>
                                        <span dangerouslySetInnerHTML={{ __html: ratesText.join('') }} />
                        </Box>
                                )}
                    </Alert>
                </TableCell>
            </TableRow>
                ))}
            <TableRow>
                    <TableCell colSpan={precell ? 4 : 3} sx={{ paddingTop: '2px', borderBottom: 'none', backgroundColor: '#ffffff' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px', alignItems: 'center' }}>
                            <Button
                                variant='text'
                                onClick={onAddMargin}
                                sx={{
                                    textTransform: 'uppercase',
                                    fontSize: '11px !important',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    color: theme.palette.text.primary,
                                    border: 'none',
                                    borderBottom: '2px solid transparent',
                                    borderRadius: 0,
                                    padding: '4px 0px',
                                    minWidth: 'auto',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    '& .MuiButton-label': {
                                        fontSize: '11px !important'
                                    },
                                    '& span': {
                                        fontSize: '11px !important'
                                    },
                                    '& .MuiButton-text': {
                                        fontSize: '11px !important'
                                    },
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                        backgroundColor: 'transparent',
                                        '& .MuiButton-label': {
                                            fontSize: '11px !important'
                                        },
                                        '& span': {
                                            fontSize: '11px !important'
                                        }
                                    }
                                }}
                            >
                                Price with Uplift
                            </Button>
                            <Button
                                variant='text'
                                onClick={onFieldSettings}
                                sx={{
                                    textTransform: 'uppercase',
                                    fontSize: '11px !important',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    color: theme.palette.text.primary,
                                    border: 'none',
                                    borderBottom: '2px solid transparent',
                                    borderRadius: 0,
                                    padding: '4px 0px',
                                    minWidth: 'auto',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    '& .MuiButton-label': {
                                        fontSize: '11px !important'
                                    },
                                    '& span': {
                                        fontSize: '11px !important'
                                    },
                                    '& .MuiButton-text': {
                                        fontSize: '11px !important'
                                    },
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                        backgroundColor: 'transparent',
                                        '& .MuiButton-label': {
                                            fontSize: '11px !important'
                                        },
                                        '& span': {
                                            fontSize: '11px !important'
                                        }
                                    }
                                }}
                            >
                                Add or Remove Costs
                            </Button>
                        </Box>
                    </TableCell>
            </TableRow>
            </>
        );
    }
}

export default OpportunityOfferTextField;

function SetMarginDialog(props) {
    const { open, onClose, onConfirm, defaultCurrency, organization } = props;
    const [ margin, setMargin ] = useState(0);
    const [ additionalPrice, setAdditionalPrice ] = useState('');
    const [ currency, setCurrency ] = useState(defaultCurrency);
    const [ focused, setFocused ] = useState(null);
    
    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            setMargin(0);
            setAdditionalPrice('');
            setCurrency(defaultCurrency);
            setFocused(null);
        }
    }, [open, defaultCurrency]);
    
    function handleBlur() {
        // No-op, CurrencyTextField handles its own blur
    }
    
    return (
        <Dialog open={open} onClose={onClose} sx={{ zIndex: 1000000 }}>
            <DialogTitle>Set Margin</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Please enter the margin percentage to add to the buying rate.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin='dense'
                    id='margin'
                    label='Margin (%)'
                    fullWidth
                    variant='outlined'
                    value={margin}
                    onChange={(e) => setMargin(e.target.value)}
                    sx={{ marginBottom: '16px' }}
                />
                <DialogContentText sx={{ marginBottom: '8px' }}>
                    Additional price (optional):
                </DialogContentText>
                <Box sx={{ width: '100%' }}>
                    <CurrencyTextField
                        value={additionalPrice}
                        setValue={setAdditionalPrice}
                        focused={focused}
                        setFocused={setFocused}
                        focusedId='additional-price'
                        currency={currency}
                        setCurrency={setCurrency}
                        onBlur={handleBlur}
                        disabled={false}
                        organization={organization}
                        forceTextField={true}
                        width='100%'
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => {
                    const priceValue = parseFloat(additionalPrice) || 0;
                    onConfirm({ 
                        margin: (parseFloat(margin) || 0) / 100,
                        additionalPrice: priceValue,
                        currency: currency
                    });
                    onClose();
                }}>Add</Button>
            </DialogActions>
        </Dialog>
    );
}
